/**
 * Audience Selector Component (Email)
 *
 * Comprehensive audience selection matching SMS campaign pattern
 * - Multi-restaurant selection
 * - Audience type (all vs filtered)
 * - Advanced filters (orders, dates, spend, loyalty)
 * - Real-time audience preview
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Users, Loader2, TrendingUp, DollarSign, CalendarIcon, Store, Check } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { enNZ } from 'date-fns/locale';
import EmailService from '@/api/email';

interface AudienceSelectorProps {
  organisationId: string;
  restaurantId?: string | null;
  userRestaurants?: any[];
  initialAudienceType?: 'all' | 'filtered' | 'custom';
  initialFilters?: Record<string, any>;
  initialSelectedRestaurants?: string[];
  onChange: (audienceData: {
    audience_type: 'all' | 'filtered' | 'custom';
    audience_filters: Record<string, any>;
    estimatedRecipients: number;
    estimatedCost: number;
    selectedRestaurants: string[];
  }) => void;
}

const AudienceSelector: React.FC<AudienceSelectorProps> = ({
  organisationId,
  restaurantId,
  userRestaurants = [],
  initialAudienceType = 'all',
  initialFilters = {},
  initialSelectedRestaurants = ['all'],
  onChange
}) => {
  const [audienceType, setAudienceType] = useState<'all' | 'filtered' | 'custom'>(initialAudienceType);
  const [selectedRestaurants, setSelectedRestaurants] = useState<string[]>(initialSelectedRestaurants);
  const [isRestaurantPopoverOpen, setIsRestaurantPopoverOpen] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({
    min_orders: 1,
    max_orders: null,
    last_order_from_date: subDays(new Date(), 365),
    last_order_to_date: new Date(),
    min_total_spent: null,
    max_total_spent: null,
    min_avg_order_value: null,
    max_avg_order_value: null,
    ...initialFilters
  });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<{
    total_recipients: number;
    estimated_cost: number;
    sample_recipients: any[];
  } | null>(null);

  // Debounce preview updates
  useEffect(() => {
    const timer = setTimeout(() => {
      handlePreview();
    }, 500);

    return () => clearTimeout(timer);
  }, [audienceType, filters, selectedRestaurants]);

  // Update filter value
  const updateFilter = useCallback((key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Handle restaurant selection
  const handleRestaurantChange = (restaurantId: string) => {
    let newSelection = [...selectedRestaurants];

    if (restaurantId === 'all') {
      newSelection = ['all'];
    } else {
      if (newSelection.includes('all')) {
        newSelection = newSelection.filter(id => id !== 'all');
      }

      if (newSelection.includes(restaurantId)) {
        if (newSelection.length > 1) {
          newSelection = newSelection.filter(id => id !== restaurantId);
        } else {
          newSelection = ['all'];
        }
      } else {
        newSelection.push(restaurantId);
      }
    }

    setSelectedRestaurants(newSelection);
  };

  // Get display value for restaurant selector
  const getRestaurantDisplayValue = () => {
    if (selectedRestaurants.includes('all')) {
      return 'All Restaurants';
    }

    if (selectedRestaurants.length === 1) {
      const restaurant = userRestaurants.find(r => r.id === selectedRestaurants[0]);
      return restaurant ? restaurant.name : 'Select Restaurant';
    }

    return `${selectedRestaurants.length} Restaurants Selected`;
  };

  // Handle audience type change
  const handleAudienceTypeChange = (type: 'all' | 'filtered' | 'custom') => {
    setAudienceType(type);

    if (type === 'all') {
      setFilters({
        min_orders: 1,
        max_orders: null,
        last_order_from_date: subDays(new Date(), 365),
        last_order_to_date: new Date(),
        min_total_spent: null,
        max_total_spent: null,
        min_avg_order_value: null,
        max_avg_order_value: null
      });
    }
  };

  // Preview audience
  const handlePreview = async () => {
    try {
      setPreviewLoading(true);

      // Convert selectedRestaurants to array for backend
      // 'all' means no filter (empty array or null)
      const restaurantIdsToSend = selectedRestaurants.includes('all')
        ? []
        : selectedRestaurants;

      const result = await EmailService.previewAudience(organisationId, {
        restaurant_ids: restaurantIdsToSend,
        audience_type: audienceType,
        audience_filters: audienceType === 'filtered' ? filters : {}
      });

      setPreviewData(result);

      // Notify parent component
      onChange({
        audience_type: audienceType,
        audience_filters: audienceType === 'filtered' ? filters : {},
        estimatedRecipients: result.total_recipients,
        estimatedCost: typeof result.estimated_cost === 'number'
          ? result.estimated_cost
          : parseFloat(result.estimated_cost),
        selectedRestaurants: selectedRestaurants
      });
    } catch (error: any) {
      console.error('Error previewing audience:', error);
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="space-y-6">
        {/* Restaurant Selector */}
        {userRestaurants && userRestaurants.length > 0 && (
          <>
            <div className="space-y-4">
              <Label>Select Restaurants</Label>
              <Popover open={isRestaurantPopoverOpen} onOpenChange={setIsRestaurantPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between"
                    type="button"
                  >
                    <div className="flex items-center gap-2">
                      <Store className="h-4 w-4" />
                      <span>{getRestaurantDisplayValue()}</span>
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[350px] bg-background" align="start">
                  <div className="py-2">
                    <div className="px-2 py-1.5 text-sm font-semibold">Select Restaurants</div>
                    <div className="-mx-1 my-1 h-px bg-muted"></div>

                    {/* All Restaurants Option */}
                    <div
                      className="relative flex items-center justify-between rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none cursor-pointer hover:bg-accent hover:text-accent-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestaurantChange('all');
                        setIsRestaurantPopoverOpen(false);
                      }}
                    >
                      <div className="flex items-center">
                        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                          {selectedRestaurants.includes('all') && (
                            <Check className="h-4 w-4" />
                          )}
                        </span>
                        All Restaurants
                      </div>
                    </div>

                    <div className="-mx-1 my-1 h-px bg-muted"></div>

                    {/* Individual Restaurants */}
                    {userRestaurants.map(restaurant => (
                      <div
                        key={restaurant.id}
                        className="relative flex items-center justify-between rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none cursor-pointer hover:bg-accent hover:text-accent-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRestaurantChange(restaurant.id);
                        }}
                      >
                        <div className="flex items-center">
                          <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                            {selectedRestaurants.includes(restaurant.id) && (
                              <Check className="h-4 w-4" />
                            )}
                          </span>
                          {restaurant.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <p className="text-sm text-muted-foreground">
                Choose which restaurants to include in your campaign
              </p>
            </div>

            <Separator />
          </>
        )}

        {/* Audience Type Selection */}
        <div className="space-y-4">
          <Label className="text-base font-semibold">Who should receive this email?</Label>
          <RadioGroup value={audienceType} onValueChange={handleAudienceTypeChange}>
            <div className="flex items-start space-x-2">
              <RadioGroupItem value="all" id="all" />
              <div className="flex-1">
                <Label htmlFor="all" className="font-medium cursor-pointer">
                  All Email Subscribers
                </Label>
                <p className="text-sm text-muted-foreground">
                  Send to everyone who has opted in to receive emails
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <RadioGroupItem value="filtered" id="filtered" />
              <div className="flex-1">
                <Label htmlFor="filtered" className="font-medium cursor-pointer">
                  Filtered Audience
                </Label>
                <p className="text-sm text-muted-foreground">
                  Target specific customer segments based on behavior
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <RadioGroupItem value="custom" id="custom" disabled />
              <div className="flex-1 opacity-50">
                <Label htmlFor="custom" className="font-medium">
                  Custom List
                </Label>
                <p className="text-sm text-muted-foreground">
                  Upload a CSV file with email addresses (Coming Soon)
                </p>
              </div>
            </div>
          </RadioGroup>
        </div>

        {/* Filters for Filtered Audience */}
        {audienceType === 'filtered' && (
          <>
            <Separator />
            <div className="space-y-6 pt-4">
              {/* Total Orders Filter */}
              <div className="space-y-3">
                <Label className="text-base">Total Orders Placed</Label>
                <div className="flex flex-col md:flex-row gap-4 w-full">
                  <div className="space-y-2 flex-1">
                    <Label htmlFor="min-orders">Minimum Orders</Label>
                    <Input
                      id="min-orders"
                      type="number"
                      min="0"
                      value={filters.min_orders || 0}
                      onChange={(e) => updateFilter('min_orders', parseInt(e.target.value) || 0)}
                      placeholder="e.g., 1"
                    />
                  </div>

                  <div className="space-y-2 flex-1">
                    <Label htmlFor="max-orders">Maximum Orders (Optional)</Label>
                    <Input
                      id="max-orders"
                      type="number"
                      min="0"
                      placeholder="Any"
                      value={filters.max_orders || ''}
                      onChange={(e) => updateFilter('max_orders', e.target.value ? parseInt(e.target.value) : null)}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Filter customers by the number of orders they've placed
                </p>
              </div>

              <Separator />

              {/* Last Order Date Filter */}
              <div className="space-y-3">
                <Label className="text-base">Last Visit Date</Label>
                <div className="flex flex-col md:flex-row gap-4 w-full">
                  <div className="space-y-2 flex-1">
                    <Label htmlFor="from-date">From</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          id="from-date"
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {filters.last_order_from_date
                            ? format(filters.last_order_from_date, 'PPP', { locale: enNZ })
                            : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={filters.last_order_from_date}
                          onSelect={(date) => updateFilter('last_order_from_date', date || null)}
                          disabled={(date) =>
                            date > new Date() ||
                            (filters.last_order_to_date && date > filters.last_order_to_date)
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2 flex-1">
                    <Label htmlFor="to-date">To</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          id="to-date"
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {filters.last_order_to_date
                            ? format(filters.last_order_to_date, 'PPP', { locale: enNZ })
                            : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={filters.last_order_to_date}
                          onSelect={(date) => updateFilter('last_order_to_date', date || null)}
                          disabled={(date) =>
                            date > new Date() ||
                            (filters.last_order_from_date && date < filters.last_order_from_date)
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Total Spend Filter */}
              <div className="space-y-3">
                <Label className="text-base">Total Spend</Label>
                <div className="flex flex-col md:flex-row gap-4 w-full">
                  <div className="space-y-2 flex-1">
                    <Label htmlFor="min-spent">Minimum Spent ($)</Label>
                    <Input
                      id="min-spent"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={filters.min_total_spent || ''}
                      onChange={(e) => updateFilter('min_total_spent', e.target.value ? parseFloat(e.target.value) : null)}
                    />
                  </div>

                  <div className="space-y-2 flex-1">
                    <Label htmlFor="max-spent">Maximum Spent ($, Optional)</Label>
                    <Input
                      id="max-spent"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Any"
                      value={filters.max_total_spent || ''}
                      onChange={(e) => updateFilter('max_total_spent', e.target.value ? parseFloat(e.target.value) : null)}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Filter customers by their total lifetime spend
                </p>
              </div>

              <Separator />

              {/* Average Order Value Filter */}
              <div className="space-y-3">
                <Label className="text-base">Average Order Value</Label>
                <div className="flex flex-col md:flex-row gap-4 w-full">
                  <div className="space-y-2 flex-1">
                    <Label htmlFor="min-avg">Minimum Avg ($)</Label>
                    <Input
                      id="min-avg"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={filters.min_avg_order_value || ''}
                      onChange={(e) => updateFilter('min_avg_order_value', e.target.value ? parseFloat(e.target.value) : null)}
                    />
                  </div>

                  <div className="space-y-2 flex-1">
                    <Label htmlFor="max-avg">Maximum Avg ($, Optional)</Label>
                    <Input
                      id="max-avg"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Any"
                      value={filters.max_avg_order_value || ''}
                      onChange={(e) => updateFilter('max_avg_order_value', e.target.value ? parseFloat(e.target.value) : null)}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Filter customers by their average order size
                </p>
              </div>
            </div>
          </>
        )}

        <Separator />

        {/* Audience Preview */}
        <div className={previewLoading ? 'opacity-50' : ''}>
          <div className="flex items-center justify-between mb-4">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Users className="h-4 w-4" />
              Audience Preview
            </Label>
            {previewLoading && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>

          {previewData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-primary/10 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-muted-foreground">
                      Estimated Recipients
                    </span>
                  </div>
                  <p className="text-2xl font-bold">
                    {previewData.total_recipients.toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    customers will receive this email
                  </p>
                </div>

                <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-muted-foreground">
                      Estimated Cost
                    </span>
                  </div>
                  <p className="text-2xl font-bold">
                    ${typeof previewData.estimated_cost === 'number'
                      ? previewData.estimated_cost.toFixed(2)
                      : parseFloat(previewData.estimated_cost).toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    at $0.0001 per email
                  </p>
                </div>
              </div>

              {/* Sample Recipients */}
              {previewData.sample_recipients && previewData.sample_recipients.length > 0 && (
                <div className="mt-4">
                  <Label className="text-sm font-medium mb-2 block">Sample Recipients</Label>
                  <div className="space-y-2">
                    {previewData.sample_recipients.slice(0, 5).map((recipient, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm"
                      >
                        <div>
                          <span className="font-medium">
                            {recipient.first_name} {recipient.last_name}
                          </span>
                          <span className="text-muted-foreground ml-2">
                            {recipient.email}
                          </span>
                        </div>
                        {recipient.total_orders && (
                          <span className="text-xs text-muted-foreground">
                            {recipient.total_orders} orders
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center border rounded-lg">
              <Users className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                {previewLoading ? 'Loading preview...' : 'Configure filters to see audience size'}
              </p>
            </div>
          )}
        </div>
    </div>
  );
};

export default AudienceSelector;
